import React, { useState } from 'react';
import { Alert } from '@trussworks/react-uswds';
import { US_STATES, SUFFIX_OPTIONS, RACE_OPTIONS, COUNTRIES, LOUISIANA_PARISHES } from '../../constants/formOptions';
import BirthCountryFields from '../BirthCountryFields';

// The applicant's own Place of Birth row does NOT offer the "Unknown" option.
// Parent birthplace rows continue to use the full US_STATES / COUNTRIES lists
// (which already include Unknown) plus their existing cascade handlers.
const OWN_US_STATES = US_STATES.filter((o) => o.value !== 'Unknown');
const OWN_COUNTRIES = COUNTRIES.filter((o) => o.value !== 'Unknown');

const PartyBForm = ({ formData, updateFormData, nextStep, prevStep }) => {
  const [ssnInput, setSsnInput] = useState(formData.partyB.ssn || '');
  const [countryNameError, setCountryNameError] = useState(false);

  const handleChange = (field, value) => {
    updateFormData({
      partyB: { ...formData.partyB, [field]: value }
    });
  };

  const handleNext = () => {
    const party = formData.partyB || {};
    if ([party.birthCountry, party.motherBirthCountry, party.fatherBirthCountry].includes('Other')) {
      setCountryNameError(true);
      return;
    }
    setCountryNameError(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    nextStep();
  };
  const handlePrev = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    prevStep();
  };

  const handleSsnChange = (e) => {
    // Get the input value and remove formatting/asterisks
    let rawValue = e.target.value;

    // If backspace was pressed or value is getting shorter
    if (rawValue.length < formatSsn().length) {
      // Remove one character from the actual SSN
      setSsnInput(ssnInput.slice(0, -1));
      handleChange('ssn', ssnInput.slice(0, -1));
      return;
    }

    // Get only the new character typed (last character)
    const newChar = rawValue.slice(-1);

    // Only accept digits
    if (/\d/.test(newChar) && ssnInput.length < 9) {
      const newValue = ssnInput + newChar;
      setSsnInput(newValue);
      handleChange('ssn', newValue);
    }
  };

  const formatSsn = () => {
    if (!ssnInput) return '';
    const length = ssnInput.length;
    if (length <= 5) {
      return '*'.repeat(length);
    }
    return '*****' + ssnInput.slice(5);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove all non-digits

    if (value.length <= 10) {
      // Format the phone number
      let formatted = '';
      if (value.length > 0) {
        formatted = '(' + value.substring(0, 3);
      }
      if (value.length >= 4) {
        formatted += ') ' + value.substring(3, 6);
      }
      if (value.length >= 7) {
        formatted += '-' + value.substring(6, 10);
      }

      handleChange('phoneNumber', formatted);
    }
  };

  const handleBirthStateChange = (value) => {
    updateFormData({
      partyB: {
        ...formData.partyB,
        birthState: value,
        birthCountry: !value || value === 'Other'
          ? (formData.partyB.birthCountry === 'US' ? '' : formData.partyB.birthCountry)
          : 'US'
      }
    });
  };

  // Handle birth country change - clear state if not US
  const handleBirthCountryChange = (value) => {
    updateFormData({
      partyB: {
        ...formData.partyB,
        birthCountry: value,
        birthState: value !== 'US' ? 'Other' : formData.partyB.birthState
      }
    });
  };

  // Handle mother birth state change - auto-select US as country
  const handleMotherBirthStateChange = (value) => {
    updateFormData({
      partyB: {
        ...formData.partyB,
        motherBirthState: value,
        motherBirthCountry: value === 'Unknown'
          ? 'Unknown'
          : (value && value !== 'Other' ? 'US' : formData.partyB.motherBirthCountry)
      }
    });
  };

  // Handle mother birth country change - clear state if not US
  const handleMotherBirthCountryChange = (value) => {
    updateFormData({
      partyB: {
        ...formData.partyB,
        motherBirthCountry: value,
        motherBirthState: value === 'Unknown'
          ? 'Unknown'
          : (value !== 'US' ? 'Other' : formData.partyB.motherBirthState)
      }
    });
  };

  // Handle father birth state change - auto-select US as country
  const handleFatherBirthStateChange = (value) => {
    updateFormData({
      partyB: {
        ...formData.partyB,
        fatherBirthState: value,
        fatherBirthCountry: value === 'Unknown'
          ? 'Unknown'
          : (value && value !== 'Other' ? 'US' : formData.partyB.fatherBirthCountry)
      }
    });
  };

  // Handle father birth country change - clear state if not US
  const handleFatherBirthCountryChange = (value) => {
    updateFormData({
      partyB: {
        ...formData.partyB,
        fatherBirthCountry: value,
        fatherBirthState: value === 'Unknown'
          ? 'Unknown'
          : (value !== 'US' ? 'Other' : formData.partyB.fatherBirthState)
      }
    });
  };

  return (
    <div>
      <h1 className="form-section-title">Party B Information</h1>

      {/* Party Type Selection */}
      <div className="form-section">
        <label className="form-label">Party Type</label>
        <div className="button-group">
          <button
            type="button"
            className={`btn ${formData.partyB.role === 'Groom' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleChange('role', 'Groom')}
          >
            Groom
          </button>
          <button
            type="button"
            className={`btn ${formData.partyB.role === 'Bride' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleChange('role', 'Bride')}
          >
            Bride
          </button>
          <button
            type="button"
            className={`btn ${formData.partyB.role === 'Spouse' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleChange('role', 'Spouse')}
          >
            Spouse
          </button>
        </div>
      </div>

      {/* Sex Selection */}
      <div className="form-section">
        <label className="form-label">Sex</label>
        <div className="button-group">
          <button
            type="button"
            className={`btn ${formData.partyB.sex === 'Male' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleChange('sex', 'Male')}
          >
            Male
          </button>
          <button
            type="button"
            className={`btn ${formData.partyB.sex === 'Female' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => handleChange('sex', 'Female')}
          >
            Female
          </button>
        </div>
      </div>

      {/* Name Information */}
      <div className="form-section">
        <h2 className="form-subsection-title">Name Information</h2>
        <div className="form-grid cols-2">
          <div className="form-field">
            <label className="form-label">Last Name</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.lastName || ''}
              onChange={(e) => handleChange('lastName', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Suffix</label>
            <select
              className="form-input"
              value={formData.partyB.suffix || ''}
              onChange={(e) => handleChange('suffix', e.target.value)}
            >
              {SUFFIX_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">First Name</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.firstName || ''}
              onChange={(e) => handleChange('firstName', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Middle Name</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.middleName || ''}
              onChange={(e) => handleChange('middleName', e.target.value)}
            />
          </div>
          <div className="form-field full-width">
            <label className="form-label">Last Name Before Marriage</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.lastNameBeforeMarriage || ''}
              onChange={(e) => handleChange('lastNameBeforeMarriage', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Residence Address */}
      <div className="form-section">
        <h2 className="form-subsection-title">Residence Address</h2>
        <div className="form-grid cols-1">
          <div className="form-field">
            <label className="form-label">Residence Address</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.residenceAddress || ''}
              onChange={(e) => handleChange('residenceAddress', e.target.value)}
            />
          </div>
        </div>
        <div className="form-grid cols-4">
          <div className="form-field">
            <label className="form-label">City</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">{formData.partyB.state === 'LA' ? 'Parish' : 'Parish/County/Country (If non-US)'}</label>
            {formData.partyB.state === 'LA' ? (
              <select
                className="form-input"
                value={formData.partyB.parish || ''}
                onChange={(e) => handleChange('parish', e.target.value)}
              >
                {LOUISIANA_PARISHES.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="form-input"
                value={formData.partyB.parish || ''}
                onChange={(e) => handleChange('parish', e.target.value)}
              />
            )}
          </div>
          <div className="form-field">
            <label className="form-label">State</label>
            <select
              className="form-input"
              value={formData.partyB.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
            >
              {US_STATES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">ZIP</label>
            <input
              type="text"
              className="form-input"
              maxLength="10"
              value={formData.partyB.zip || ''}
              onChange={(e) => handleChange('zip', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Birth Information */}
      <div className="form-section">
        <h2 className="form-subsection-title">Birth Information</h2>
        <div className="form-grid cols-2">
          <div className="form-field">
            <label className="form-label">Race</label>
            <select
              className="form-input"
              value={formData.partyB.race || ''}
              onChange={(e) => handleChange('race', e.target.value)}
            >
              {RACE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">Date of Birth (mm/dd/yyyy)</label>
            <input
              type="date"
              className="form-input"
              value={formData.partyB.dateOfBirth || ''}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            />
          </div>
        </div>
        <div className="form-grid cols-3">
          <div className="form-field">
            <label className="form-label">Place of Birth - City</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.birthCity || ''}
              onChange={(e) => handleChange('birthCity', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Place of Birth - State</label>
            <select
              className="form-input"
              value={formData.partyB.birthState || ''}
              onChange={(e) => handleBirthStateChange(e.target.value)}
            >
              {OWN_US_STATES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <BirthCountryFields
            options={OWN_COUNTRIES}
            value={formData.partyB.birthCountry || ''}
            onChange={handleBirthCountryChange}
          />
        </div>
      </div>

      {/* Parent Information - Mother/Parent */}
      <div className="form-section">
        <h2 className="form-subsection-title">Mother/Parent Information</h2>
        <div className="form-grid cols-1">
          <div className="form-field">
            <label className="form-label">Mother/Parent's Name (Before first marriage)</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.motherName || ''}
              onChange={(e) => handleChange('motherName', e.target.value)}
            />
          </div>
        </div>
        <div className="form-grid cols-3">
          <div className="form-field">
            <label className="form-label">Place of Birth - City</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.motherBirthCity || ''}
              onChange={(e) => handleChange('motherBirthCity', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Place of Birth - State</label>
            <select
              className="form-input"
              value={formData.partyB.motherBirthState || ''}
              onChange={(e) => handleMotherBirthStateChange(e.target.value)}
            >
              {US_STATES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <BirthCountryFields
            options={COUNTRIES}
            value={formData.partyB.motherBirthCountry || ''}
            onChange={handleMotherBirthCountryChange}
          />
        </div>
      </div>

      {/* Parent Information - Father/Parent */}
      <div className="form-section">
        <h2 className="form-subsection-title">Father/Parent Information</h2>
        <div className="form-grid cols-1">
          <div className="form-field">
            <label className="form-label">Father/Parent's Name (Before first marriage)</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.fatherName || ''}
              onChange={(e) => handleChange('fatherName', e.target.value)}
            />
          </div>
        </div>
        <div className="form-grid cols-3">
          <div className="form-field">
            <label className="form-label">Place of Birth - City</label>
            <input
              type="text"
              className="form-input"
              value={formData.partyB.fatherBirthCity || ''}
              onChange={(e) => handleChange('fatherBirthCity', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label className="form-label">Place of Birth - State</label>
            <select
              className="form-input"
              value={formData.partyB.fatherBirthState || ''}
              onChange={(e) => handleFatherBirthStateChange(e.target.value)}
            >
              {US_STATES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <BirthCountryFields
            options={COUNTRIES}
            value={formData.partyB.fatherBirthCountry || ''}
            onChange={handleFatherBirthCountryChange}
          />
        </div>
      </div>

      {/* Contact Information */}
      <div className="form-section">
        <h2 className="form-subsection-title">Contact Information</h2>
        <div className="form-grid cols-2">
          <div className="form-field">
            <label className="form-label">Social Security Number</label>
            <input
              type="text"
              className="form-input"
              value={formatSsn()}
              onChange={handleSsnChange}
              placeholder="XXX-XX-XXXX"
              maxLength="9"
              autoComplete="off"
            />
            <small className="form-help-text">Last 4 digits will be visible</small>
          </div>
          <div className="form-field">
            <label className="form-label">Phone Number</label>
            <input
              type="tel"
              className="form-input"
              value={formData.partyB.phoneNumber || ''}
              onChange={handlePhoneChange}
              placeholder="(XXX) XXX-XXXX"
              maxLength="14"
            />
          </div>
        </div>
        <div className="form-field">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={formData.partyB.ssnConfidential || false}
              onChange={(e) => handleChange('ssnConfidential', e.target.checked)}
            />
            Keep Social Security Number Confidential
          </label>
        </div>
      </div>

      {countryNameError && (
        <Alert type="error" headingLevel="h4" slim className="margin-top-2">
          Enter the country name for each Place of Birth marked Other (Not listed).
        </Alert>
      )}

      <div className="step-navigation">
        <button onClick={handlePrev} className="btn btn-secondary">Back</button>
        <button onClick={handleNext} className="btn btn-primary">Next</button>
      </div>
    </div>
  );
};

export default PartyBForm;
